import { Paged } from './Misc';
import { HighlightCategory } from './Operation';

export interface GrievanceFormContextType {
  genders: Array<{ id: number; name: string }>;
  countriesClaimant: Array<{ id: number; code: string; name: string }>;
  countries: Array<{ id: number; code: string; name: string }>;
  // countriesGrievance: Array<{ id: number; name: string }>;
  channels: Array<{ id: number; name: string }>;
  origins: Array<{ id: number; name: string }>;
  mainTopics: Array<{ id: number; name: string }>;
  subTopics: Array<{ id: number; name: string; mainTopicId: number }>;
  priorities: Array<{ id: number; name: string }>;
  imminentRiskTypes: Array<{ id: number; name: string }>;
  // internalPrioritizations: Array<{ id: number; name: string }>;
  currentPhases: Array<{ id: number; name: string }>;
  loading: boolean;
  reload: () => void;
}

export interface GrievanceFilterGroup {
  queryGroup: QueryGroup;
  querySubGroups: QuerySubGroup[];
}

export enum QueryGroup {
  All = 'ALL',

  InProgress = 'InProgress',

  ClosedAndNotApplicable = 'ClosedAndNotApplicable',
}
export enum QuerySubGroup {
  All = 'ALL',

  Applicability = 'Applicability',
  AssessmentAndResponse = 'A&R',
  Monitoring = 'Monitoring',

  Closed = 'Closed',
  NotApplicable = 'N/A',
}

export interface GrievanceFilterBase {
  queryGroup: string;
  querySubGroup: string;
}

export interface GrievanceFilter extends GrievanceFilterBase {
  pageNumber: number;
  pageSize: number;
  sortDescending: boolean;
  sortField: string;
  operationId?: number;
  hiddenColumns: string[];
  year?: number;
}

export interface GrievanceList extends Paged<GrievanceItem> {
  highlightGroups: HighlightCategory[];
}

export interface GrievanceItem {
  id: number;
  number: string;
  claimantName: string;
  grievanceGender: string;
  countryClaimantOfResidence: string;
  emailClaimant: string;
  phoneClaimant: string;
  grievanceDescription: string;
  isApplicable: boolean | null;
  grievanceStatus: string;
  currentPhaseName: 'Applicability' | 'A&R' | 'Monitoring' | 'Closed' | 'N/A';
  registrationDate: string;
  grievanceTimeElapsed: number;
  programOrPrjNumber: number | null;
  programOrPrjCountry: string;
  programOrPrjName: string;
  operationCountry: string;
  mainTopicName: string;
  subTopicName: string;
  secondarySubTopicName: string;
  trackType: string;
  prioritization: string;
  isConfidential: boolean;
  originPrimary: string;
  originSecondary: string;
  channelPrimary: string;
  channelSecondary: string;
  isPGM: boolean;
  noPGMReason: string;
  isInminentRisk: boolean;
  imminentRiskTypes: string;
  imminentRiskTypeOther: string;
  isConfidentialityChange: boolean;
  confidentialityChangeDate: string | null;
  confidentialityChangeReason: string;
  isGroupClaim: boolean;
  groupClaimNumber: number;
  groupDialogueProcess: boolean;
  relateds: GrievanceRelated[];
  isReopenedCase: boolean;
  previousGrievanceNumber: string;
  previousGrievanceId: number;
  primaryGrievanceNumber: string;
  primaryGrievanceId: number;
  operationNumber: string;
  operationId: number;
  operationName: string;
  operationSector: string;
  operationSubSector: string;
  region: string;
  operationContractor: string;
  executingAgency: string;
  operationTeamLeader: string;
  operationPrimaryMember: string;
  operationMembers: string;
  operationEsic: string;
  operationDccrc: string;
  operationEsrr: string;
  operationSpr: string;
  operationSprDate: string;
  operationSprRationale: string;
  previousSPR: string;
  previousSPRDate: string;
  previousSPRRationale: string;
  espsPoliciesWithRequirements: string;
  operationStatus: string;
  idbAmount: number | null;
  lastDisbDate: string;
  totalProjectCost: number | null;
  disbursedPercentage: number;
  lendingTypeModality: string;
  miciCases: string;
  confirmationOfRegistryToComplainant: string;
  applicabilityNotificationDate: string;
  listeningSpaceDate: string;
  agreementOnActionPlanDate: string;
  isReformulated: string | null;
  cofinancing: string | null;
  monitoringNotificationDate: string;
  receptionOfActionPlanDocumentDate: string;
  verificationWithComplainantDate: string;
  authorizationOfClosure: string;
  closureNotificationDate: string;
  closureType: string;
  additionalActions?: boolean;
  isClaimantMICIInformed?: boolean;
  handledByMICIAfterwards?: boolean;
  miciHighlightDataRows: MiciHighlightDataRow[];
}

export interface GrievanceBasicItem {
  id: number;
  number: string;
  hasPrimary: boolean;
}

export interface GrievanceForm {
  id: number;
  notEditable: boolean;
  number: string;
  currentPhaseId: number | null;
  isApplicable: boolean | null;
  // 1. Complainant Information
  claimantName: string;
  claimantLastName: string;
  grievanceGenderId: number;
  emailClaimant: string;
  countryClaimantId: number;
  phoneClaimant: string;

  // 2.Grievance Operation Information provided by Complainant
  programOrPrjNumber: string;
  programOrPrjCountryId: number;
  programOrPrjName: string;

  // 3. Grievance Overview
  operationId: number | null;
  operationNumber: string;
  operationName: string;
  operationCountryCode: string;
  operationCountryId: number | null;
  grievanceDescription: string;
  receptionOriginPrimaryId: number;
  receptionOriginSecondaryId: number;
  receptionChannelPrimaryId: number;
  receptionChannelSecondaryId: number;

  contractor: string;
  isPGM: boolean | null;
  noPGMReason: string;
  prioritization: string;
  mainTopicId: number;
  subTopicId: number;
  secondarySubTopicId: number;

  // 4
  trackType: string;
  isInminentRisk: boolean | null;
  imminentRiskTypes: number[];
  imminentRiskTypeOther: string;

  isInitialConfidentiality: boolean;
  isConfidentialityChange: boolean;
  confidentialityChangeDate: Date | null;
  confidentialityChangeReason: string;

  isGroupClaim: boolean;
  groupClaimNumber: number | null;
  isGroupDialogueProcess: boolean;
  primaryGrievanceId: number | null;
  primaryGrievanceNumber: string;
  primaryGrievanceHasPrimary: boolean | null;
  isReopenedCase: boolean;
  previousGrievanceId: number | null;
  previousGrievanceNumber: string;
  isPrimary: boolean;
  passedApplicability: boolean;
}

export interface GrievanceSaveResult {
  grievanceNumber: string;
  documentsWarning?: string;
}

export interface GrievancePhase {
  phase: 'Applicability' | 'A&R' | 'Monitoring' | 'Closed' | 'N/A';
  phaseId: number;
  isCurrent: boolean;
  milestones: GrievanceMilestone[];
  logbookItems: GrievanceLogbookItem[];
}

export interface GrievanceMilestone {
  code: string;
  name: string;
  date: string | null;
  showInTimeline: boolean;
  generalOrder: number;
  isRequired: boolean;
}

export interface GrievancePhaseDataApply {
  currentPhaseName: 'Applicability' | 'A&R' | 'Monitoring' | 'Closed' | 'N/A';
  currentStatus: string;
  isApplicable?: boolean;
  closureTypeId?: number;
  additionalActions?: boolean;
  isClaimantMICIInformed?: boolean;
  handledByMICIAfterwards?: boolean;
  isDiscontinued?: boolean;
}
export interface GrievanceDetailsData {
  detailData: GrievancePhaseDataApply;
  phases: GrievancePhase[];
  lookupsClosureTypes: ClosureTypeDto[];
  lookupsDocumentTypes: GrievanceDocumentType[];
}

export interface GrievanceDetailsContext {
  grievanceNumber: string;
  grievance: GrievanceItem;
  grievancePhaseData: GrievancePhaseDataApply;
  phases: GrievancePhase[];
  lookupsClosureTypes: ClosureTypeDto[];
  lookupsDocumentTypes: GrievanceDocumentType[];
  loading: boolean;
  reload: () => void;
  reloadPhases: () => void;
}

export const GRIEVANCE_MILESTONES_CODES = {
  REGISTRATION: 'Registration',
  CONFIRMATION_OF_REGISTRY_TO_COMPLAINANT: 'ConfirmOfRegistryToComplainant',
  APPLICABILITY_TO_COMPLAINANT: 'ApplicabilityToComplainant',

  LISTENING_SPACE: 'ListeningSpace',
  AGREEMENT_ON_ACTION_PLAN: 'AgreementOnActionPlan',

  MONITORING_NOTIFICATION: 'MonitoringNotification',
  RECEPTION_OF_ACTION_PLAN: 'ReceptionOfActionPlan',
  VERIFICATION: 'Verification',

  AUTHORIZATION_OF_CLOSURE: 'AuthorizationOfClosure',
  CLOSURE_NOTIFICATION: 'ClosureNotification',
};

export const GRIEVANCE_CLOSURE_TYPES = {
  WITHDRAW: 'Withdrawn',
  NO_AGREEMENT: 'No Agreement',
  NO_RESPONSE: 'No Response',
};

export const GRIEVANCE_STATUSES = {
  IN_PROGRESS: 'InProgress',
  CLOSED: 'Closed',
  NOT_APPLICABLE: 'N/A',
};

export interface GrievanceRelated {
  id: number;
  number: string;
}

export interface GrievanceDocumentType {
  id: number;
  code: string;
  name: string;
  isTemplate: boolean;
}

export interface GrievanceLogbookForm {
  grievanceId: number;
  documentTypeId?: number;
  phaseId: number;
  comment: string;
  fromTemplate: boolean;
  formData?: FormData;
  overrideComment?: boolean;
}

export interface GrievanceLogbookItem {
  id: number;
  createdBy: string;
  type: string;
  documentTypeId?: number;
  createdDate: string;
  comments: string;
  isFinal: boolean;
  documentName?: string;
  documentLink?: string;
  canEdit: boolean;
  canDelete: boolean;
  canSetIsFinal: boolean;
  phaseId: number;
  fromTemplate?: boolean;
  approveRequired: boolean;
}

export interface GrievanceCommentExist {
  currentCommentId?: number;
  canOverride: boolean;
}

export interface MiciHighlightDataRow {
  id: string;
  number: string;
  status: string;
  closingDate: string;
}

export interface ClosureTypeDto {
  id: number;
  name: string;
  additionalActionsRequired: boolean;
  authorizationOfClosureRequired: boolean;
  claimantInformedAboutMiciRequired: boolean;
  isDiscontinueManagement: boolean;
}
